import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import React, { FC } from 'react';
import postDrugSearch, {
  PostDrugRequest,
  PostDrugResponse
} from 'src/api/requests/postDrugSearch';
import { getSearchRequestObject } from 'src/api/hooks/usePostSearchFilters';
import Head from 'next/head';
import getCityByTranslitName from 'src/api/requests/getCityByTranslitName';
import {
  AllAnalogues,
  TopDrug,
  Category,
  UserLocation,
  FetchAttr,
  TradenameItem,
  DrugReview,
  OtherTradeName
} from 'src/api/types';
import {
  generateCookieString,
  largeExpirationCookieOpts,
  locationHasBeenAskedCookieName,
  selectedLocationCookieName
} from 'src/utils/cookieHelper';
import {
  CustomErrorProps,
  triggerCustomStatusCodeRedirect,
  useHandleCustomErrorRedirect
} from 'src/ssr/errorStatusCodeRedirect';
import {
  GeneralServerProps,
  generalSSRProps,
  isQueryParamsValid,
  setSSRCacheControl
} from 'src/ssr/generalSsrUtil';
import { retrieveUserLocationCookieData } from 'src/api/utils';
import TradenameContainer from 'src/page-components/tradename/TradenameContainer';
import { isNoIndexing } from 'src/utils/seo';
import getTradenamePage from 'src/api/requests/getTradenamePage';
import logger from 'src/utils/logger';
import { getTranslitNameCityAndPage } from '../../../utils/urlParamsHelper';

const preloadDateOfCity = async (
  res,
  translitNameOfCity: string,
  attr?: FetchAttr
): Promise<UserLocation> => {
  let cityName = translitNameOfCity;
  if (!cityName || cityName === '') {
    const userSelectedCity =
      (await retrieveUserLocationCookieData(attr))?.translitName || '';
    if (!userSelectedCity || userSelectedCity === '') {
      return null;
    }
    // do not send request to API if city wasn't found
    cityName = userSelectedCity;
  }
  try {
    const city = await getCityByTranslitName(
      {
        translitName: cityName
      },
      attr
    );

    if (city) {
      try {
        res.setHeader('Set-Cookie', [
          generateCookieString({
            [selectedLocationCookieName]: encodeURI(JSON.stringify(city)),
            path: '/',
            ...largeExpirationCookieOpts
          }),
          generateCookieString({
            [locationHasBeenAskedCookieName]: true,
            path: '/',
            ...largeExpirationCookieOpts
          })
        ]);
      } catch (err) {
        logger.log(err);
      }

      return city;
    }
  } catch (e) {
    return null;
  }
  return null;
};

interface Props extends GeneralServerProps, CustomErrorProps {
  searchDrugs: PostDrugResponse;
  pageNumber: number;
  categoriesList: Category[];
  analoguesList: AllAnalogues;
  otherTradeNamesList: OtherTradeName[];
  topDrugs: TopDrug[];
  selectedCity?: UserLocation;
  citiesList?: UserLocation[];
  tradenameData: TradenameItem;
  translitNameOfCity?: string;
  reviewsList: DrugReview[];
  isSorting: boolean;
  ssrSearchObject: PostDrugRequest;
}

const TradenamePage: FC<Props> = ({
  searchDrugs,
  pageNumber,
  hasError,
  statusCode,
  analoguesList,
  otherTradeNamesList,
  topDrugs,
  selectedCity,
  citiesList,
  tradenameData,
  reviewsList,
  isSorting,
  ssrSearchObject
}) => {
  const { redirectToStatusCodePage } = useHandleCustomErrorRedirect({
    hasError,
    statusCode
  });
  if (hasError) {
    return redirectToStatusCodePage();
  }

  const getIndex = () => {
    if (pageNumber > 1 || isSorting) return 'noindex, follow';
    if (isNoIndexing) return 'noindex';
    return 'index, follow';
  };

  return (
    <>
      <Head>
        <meta name="robots" content={getIndex()} key="robots" />
      </Head>
      <TradenameContainer
        searchDrugs={searchDrugs}
        analoguesList={analoguesList}
        ssrTopDrugs={topDrugs}
        selectedCity={selectedCity}
        citiesList={citiesList}
        tradenameData={tradenameData}
        reviewsList={reviewsList}
        otherTradeNamesList={otherTradeNamesList}
        ssrSearchObject={ssrSearchObject}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>
) => {
  setSSRCacheControl(context);
  const {
    res,
    req: { cookies },
    query
  } = context;

  const generalProps = await generalSSRProps(context);
  const { nextParams, drug, filters, sortBy, sortDirection } = query;

  if (!isQueryParamsValid(query)) {
    // Redirect to 410 because the URL contains invalid query params
    return triggerCustomStatusCodeRedirect(res, 410);
  }

  const commonAttr = {
    cookies,
    ip: generalProps.ip,
    locale: context?.locale,
    useCache: true
  };

  const { translitNameOfCity, pageNumber } =
    getTranslitNameCityAndPage(nextParams);

  const selectedCity = await preloadDateOfCity(
    res,
    translitNameOfCity,
    commonAttr
  );

  const tradenamePage = await getTradenamePage(
    {
      translitName: Array.isArray(drug) ? drug[0] : drug,
      attr: commonAttr
    },
    !!translitNameOfCity
  );

  const {
    tradeName: tradenameData,
    topDrugs,
    analogues: analoguesList,
    rates: reviewsList,
    otherTradeNames: otherTradeNamesList,
    tradeNameCities: citiesList
  } = tradenamePage ?? {
    tradeName: null,
    topDrugs: [],
    analogues: [],
    rates: [],
    otherTradeNames: [],
    tradeNameCities: []
  };

  const searchObject = getSearchRequestObject(
    pageNumber,
    null,
    filters,
    sortBy,
    sortDirection
  );

  const isSorting = !!sortBy || !!sortDirection;

  // a search object used for initial load
  // it's utilized later on the client side to reduce calls to API
  const ssrSearchObject = {
    ...searchObject,
    filters: [
      {
        id: 'tradeName',
        valueIds: [tradenameData?.name]
      },
      ...searchObject.filters
    ]
  };

  const searchDrugsReq = postDrugSearch(ssrSearchObject, commonAttr);

  const locationReq = retrieveUserLocationCookieData(commonAttr);

  const [searchDrugs, location] = await Promise.all([
    searchDrugsReq,
    locationReq
  ]);

  if (!searchDrugs || !tradenameData) {
    // Redirect to 410 if drugs or tradename data not found
    return triggerCustomStatusCodeRedirect(res, 410);
  }

  return {
    props: {
      ...generalProps,
      searchDrugs,
      city: location?.name || null,
      selectedCity,
      citiesList,
      useCache: true,
      otherTradeNamesList,
      pageNumber,
      analoguesList,
      topDrugs,
      tradenameData,
      reviewsList,
      isSorting,
      ssrSearchObject
    }
  };
};

export default TradenamePage;
